import {
    faClock,
    faLocationArrow,
    faLocationDot,
} from '@fortawesome/pro-regular-svg-icons';
import { usePlacesAutocomplete } from '../../hooks/usePlacesAutoComplete';
import {
    AutocompleteOption,
    AutocompleteTypes,
    DeviceGeolocationStatus,
    getPageUrl,
    TypeAheadInput,
    useAnalyticsClickEvent,
    useAnalyticsFilterEvent,
    useDeviceGeolocation,
    useMap,
} from 'rwl-ui';
import { useEffect, useRef, useState } from 'react';
import { useGlobal } from '../../hooks/useGlobal';
import { ClickTargets } from 'rwl-shared/dist/models/analytics';
import { NeighborhoodStatus, useMapContext } from '../../hooks/MapProvider';
import { useContentDetails } from '../../providers';

const stripUSA = (address: string | undefined): string => {
    if (!address) return '';
    return address.replace(/, USA$/, '');
};

export const SearchBar = (): JSX.Element => {
    const { setApiKey } = useMap();
    const { config } = useGlobal();
    const sendClickEvent = useAnalyticsClickEvent();
    const sendFilterEvent = useAnalyticsFilterEvent();
    const { setNeighborhood, neighborhoodStatus, setNeighborhoodStatus } =
        useMapContext();
    const { closeDetails } = useContentDetails();
    // Store pending suggestion to add to recent results only if jobs are found
    const pendingSuggestionRef =
        useRef<google.maps.places.QueryAutocompletePrediction | null>(null);
    // Track current search input value
    const [searchValue, setSearchValue] = useState('');

    const {
        setQuery,
        suggestions,
        recentResults,
        addRecentResult,
        setSelectedPlaceId,
        clearSelectedPlaceId,
        selectedPlaceDetails,
    } = usePlacesAutocomplete();

    // Add to recent results only when neighborhood is found (has jobs)
    useEffect(() => {
        if (
            neighborhoodStatus === NeighborhoodStatus.FOUND &&
            pendingSuggestionRef.current
        ) {
            addRecentResult(pendingSuggestionRef.current);
            pendingSuggestionRef.current = null;
        } else if (neighborhoodStatus === NeighborhoodStatus.NOT_FOUND) {
            // Don't add to recent results if no jobs found
            pendingSuggestionRef.current = null;
        }
    }, [neighborhoodStatus, addRecentResult]);
    const { requestGeolocation } = useDeviceGeolocation();

    const getPlaceIdFromCurrentLocation = () => {
        requestGeolocation((location) => {
            if (location) {
                const geocodingService = new google.maps.Geocoder();
                geocodingService.geocode(
                    {
                        location: {
                            lat: location.coords.latitude,
                            lng: location.coords.longitude,
                        },
                    },
                    (results, status) => {
                        if (
                            status === google.maps.GeocoderStatus.OK &&
                            results?.[0]
                        ) {
                            const placeId = results[0].place_id;
                            setSelectedPlaceId(placeId);
                        }
                    }
                );
            }
        });
    };

    useEffect(() => {
        if (config && config.googleMapsKey) {
            setApiKey(config.googleMapsKey);
        }
    }, [config]);

    useEffect(() => {
        if (selectedPlaceDetails) {
            const postalComponent =
                selectedPlaceDetails.address_components?.find((component) =>
                    component.types.includes('postal_code')
                );
            if (postalComponent?.short_name) {
                const zipcode = postalComponent.short_name;
                sendFilterEvent({
                    filter: zipcode,
                    wasAdded: true,
                });
            }
        }
    }, [selectedPlaceDetails]);

    // Only show Google suggestions when search bar has text (user is actively typing)
    // Also filter out suggestions that already exist in recent results
    const recentPlaceIds = new Set(recentResults.map((r) => r.place_id));
    const filteredSuggestions =
        searchValue.length > 3
            ? suggestions.filter((s) => !recentPlaceIds.has(s.place_id))
            : [];

    const locationSuggestions: AutocompleteOption[] = [
        ...recentResults.map((r) => ({
            type: AutocompleteTypes.RECENT,
            key: r.place_id || r.description,
            label: stripUSA(r.description),
            icon: faClock,
            onSelected: () => {
                setSearchValue('');
                setNeighborhoodStatus(NeighborhoodStatus.NOT_SET);
                setSelectedPlaceId(r.place_id);
                sendClickEvent({
                    url: getPageUrl(),
                    target: ClickTargets.SEARCH_FINISHED,
                });
            },
        })),
        ...filteredSuggestions.map((s) => ({
            type: AutocompleteTypes.SUGGESTION,
            key: s.place_id || s.description,
            label: stripUSA(s.description),
            icon: faLocationDot,
            onSelected: () => {
                setSearchValue('');
                // Store suggestion to add to recent results only if jobs are found
                pendingSuggestionRef.current = s;
                // Reset status so the useEffect will trigger when results come back
                setNeighborhoodStatus(NeighborhoodStatus.NOT_SET);
                setSelectedPlaceId(s.place_id);
                sendClickEvent({
                    url: getPageUrl(),
                    target: ClickTargets.SEARCH_FINISHED,
                });
            },
        })),
    ];

    if (status !== DeviceGeolocationStatus.Unavailable) {
        locationSuggestions.unshift({
            type: AutocompleteTypes.CURRENT,
            key: 'current',
            label: 'Current location',
            icon: faLocationArrow,
            onSelected: () => {
                setSearchValue('');
                setNeighborhoodStatus(NeighborhoodStatus.NOT_SET);
                sendClickEvent({
                    url: getPageUrl(),
                    target: ClickTargets.SEARCH_FINISHED,
                });
                getPlaceIdFromCurrentLocation();
            },
        });
    }
    return (
        <div style={{ width: '100%' }}>
            <TypeAheadInput
                smallSize={false}
                prefilledValue={stripUSA(
                    selectedPlaceDetails?.formatted_address
                )}
                suggestions={locationSuggestions}
                onTyping={(value) => {
                    setSearchValue(value);
                    if (value.length > 3) {
                        setQuery(value);
                    }
                }}
                onClear={() => {
                    clearSelectedPlaceId();
                    setNeighborhood(undefined);
                    closeDetails();
                }}
                onFocus={() => {
                    sendClickEvent({
                        url: getPageUrl(),
                        target: ClickTargets.SEARCH_FOCUSED,
                    });
                }}
            />
        </div>
    );
};
